/* ═══════════════════════════════════════════
   OMNISM JEWELRY — Theme JavaScript
   ═══════════════════════════════════════════ */

(function () {
  'use strict';

  /* ─── CART STATE ─── */
  let cart = { items: [], item_count: 0, total_price: 0 };

  /* ─── HELPERS ─── */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }

  /* Escape HTML special chars before injecting user data into innerHTML */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Only allow relative paths and http(s) URLs in href/src attributes */
  function safeUrl(url) {
    const s = String(url);
    return /^(https?:)?\//.test(s) ? s : '#';
  }

  function openModal(el) {
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeModal(el) {
    el.classList.remove('open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ─── CART FETCH ─── */
  async function fetchCart() {
    const res = await fetch('/cart.js');
    cart = await res.json();
    return cart;
  }

  async function cartAdd(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }

  async function cartChange(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    cart = await res.json();
    return cart;
  }

  async function cartRemove(key) {
    return cartChange(key, 0);
  }

  /* ─── RENDER CART DRAWER ─── */
  function renderCartDrawer() {
    const body = qs('#cart-drawer-body');
    const countEls = qsa('#cart-count, #cart-drawer-count');
    const subtotalEl = qs('#cart-subtotal');
    const shippingProgressEl = qs('#cart-shipping-progress');
    if (!body) return;

    countEls.forEach(el => { el.textContent = cart.item_count; });
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    if (shippingProgressEl) {
      const remaining = Math.max(0, 5500 - cart.total_price);
      shippingProgressEl.textContent = remaining > 0
        ? 'You’re ' + money(remaining) + ' away from free shipping.'
        : 'You’ve unlocked free U.S. shipping.';
    }

    if (cart.items.length === 0) {
      body.innerHTML = '<p class="cart-drawer-empty">YOUR BAG IS EMPTY</p>';
      return;
    }

    body.innerHTML = cart.items.map(item => `
      <div class="cart-drawer-item" data-key="${esc(item.key)}">
        <a href="${safeUrl(item.url)}" class="cart-drawer-item-img">
          ${item.image ? `<img src="${safeUrl(item.image)}" alt="${esc(item.title)}" loading="lazy">` : ''}
        </a>
        <div>
          <a href="${safeUrl(item.url)}" class="cart-drawer-item-title">${esc(item.product_title).toUpperCase()}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer-item-variant">${esc(item.variant_title).toUpperCase()}</p>` : ''}
          <div class="cart-drawer-item-bottom">
            <span class="cart-drawer-item-price">${money(item.line_price)}</span>
            <div class="cart-drawer-item-actions">
              <button class="cart-drawer-qty-btn" data-action="decrease" data-key="${esc(item.key)}">−</button>
              <span class="cart-drawer-qty">${item.quantity}</span>
              <button class="cart-drawer-qty-btn" data-action="increase" data-key="${esc(item.key)}">+</button>
              <button class="cart-drawer-remove" data-key="${esc(item.key)}" aria-label="Remove">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `).join('');

    /* qty / remove handlers inside drawer */
    qsa('.cart-drawer-qty-btn', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = action === 'increase' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
        await cartChange(key, newQty);
        renderCartDrawer();
      });
    });

    qsa('.cart-drawer-remove', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        await cartRemove(btn.dataset.key);
        renderCartDrawer();
      });
    });
  }

  /* ─── CART DRAWER OPEN/CLOSE ─── */
  const cartDrawer = qs('#cart-drawer');
  const cartToggle = qs('#cart-toggle');
  const cartClose = qs('#cart-close');
  const cartOverlay = qs('#cart-overlay');

  function openCartDrawer() {
    fetchCart().then(() => {
      renderCartDrawer();
      openModal(cartDrawer);
    });
  }

  if (cartToggle) cartToggle.addEventListener('click', openCartDrawer);
  if (cartClose) cartClose.addEventListener('click', () => closeModal(cartDrawer));
  if (cartOverlay) cartOverlay.addEventListener('click', () => closeModal(cartDrawer));

  /* ─── CART PAGE QTY / REMOVE ─── */
  qsa('.cart-qty-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key = btn.dataset.key;
      const action = btn.dataset.action;
      const row = btn.closest('.cart-item');
      const display = row.querySelector('.cart-qty-display');
      let qty = parseInt(display.textContent, 10);
      qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
      await cartChange(key, qty);
      if (qty === 0) {
        row.remove();
      } else {
        display.textContent = qty;
      }
      const subtotal = qs('#cart-page-subtotal');
      if (subtotal) {
        const c = await fetchCart();
        subtotal.textContent = money(c.total_price);
      const progress = qs('.cart-page .cart-shipping-progress');
      if (progress) {
        const remaining = Math.max(0, 5500 - c.total_price);
        progress.textContent = c.item_count === 0
          ? ''
          : remaining > 0
            ? 'You’re ' + money(remaining) + ' away from free shipping.'
            : 'You’ve unlocked free U.S. shipping.';
      }
      const headerCount = qs('#cart-count');
      if (headerCount) headerCount.textContent = c.item_count;
      }
    });
  });

  qsa('.cart-item-remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      await cartRemove(btn.dataset.key);
      btn.closest('.cart-item')?.remove();
      const c = await fetchCart();
      const subtotal = qs('#cart-page-subtotal');
      if (subtotal) subtotal.textContent = money(c.total_price);
      const progress = qs('.cart-page .cart-shipping-progress');
      if (progress) {
        const remaining = Math.max(0, 5500 - c.total_price);
        progress.textContent = c.item_count === 0
          ? ''
          : remaining > 0
            ? 'You’re ' + money(remaining) + ' away from free shipping.'
            : 'You’ve unlocked free U.S. shipping.';
      }
      const headerCount = qs('#cart-count');
      if (headerCount) headerCount.textContent = c.item_count;
    });
  });

  /* ─── SEARCH MODAL ─── */
  const searchModal = qs('#search-modal');
  const searchToggle = qs('#search-toggle');
  const searchClose = qs('#search-close');
  const searchOverlay = qs('#search-overlay');
  const searchInput = qs('#search-input');

  function openSearch() {
    openModal(searchModal);
    setTimeout(() => searchInput?.focus(), 100);
  }

  if (searchToggle) searchToggle.addEventListener('click', openSearch);
  if (searchClose) searchClose.addEventListener('click', () => closeModal(searchModal));
  if (searchOverlay) searchOverlay.addEventListener('click', () => closeModal(searchModal));

  /* Live search suggestions */
  let searchTimer;
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(searchTimer);
      const q = searchInput.value.trim();
      if (q.length < 2) {
        qs('#search-suggestions').innerHTML = '';
        return;
      }
      searchTimer = setTimeout(async () => {
        try {
          const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          const sugEl = qs('#search-suggestions');
          if (!sugEl) return;
          sugEl.innerHTML = products.map(p =>
            `<a href="${safeUrl(p.url)}" class="search-suggestion">${esc(p.title).toUpperCase()}</a>`
          ).join('');
        } catch (_) {}
      }, 300);
    });
  }

  /* ─── QUICK VIEW ─── */
  const qvModal = qs('#quick-view-modal');
  const qvContent = qs('#quick-view-content');
  const qvClose = qs('#quick-view-close');
  const qvOverlay = qs('#quick-view-overlay');

  if (qvClose) qvClose.addEventListener('click', () => closeModal(qvModal));
  if (qvOverlay) qvOverlay.addEventListener('click', () => closeModal(qvModal));

  async function openQuickView(handle, embeddedData) {
    if (!qvModal || !qvContent) return;
    qvContent.innerHTML = '<div class="quick-view-loading"><div class="spinner"></div></div>';
    openModal(qvModal);

    try {
      let product;

      if (embeddedData) {
        product = embeddedData;
        product.images = product.image ? [{ src: product.image }] : [];
      } else {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('json')) throw new Error('Not JSON — store may be password protected');
        product = await res.json();
      }

      const variant = product.variants[0];
      const img = product.images[0];

      const imgSrc = img ? (img.src || img) : null;
      const productUrl = product.url || `/products/${product.handle}`;
      const hasVariants = product.variants && product.variants.length > 1;
      const firstOption = (product.options && product.options[0]) || 'SIZE';

      /* Process description — turn care info text into a trigger button */
      let rawDesc = product.description || '';
      let truncDesc = rawDesc.substring(0, 300) + (rawDesc.length > 300 ? '…' : '');
      truncDesc = truncDesc.replace(
        /(?:⚠️\s*)?sterling silver care\s*(?:&amp;|&)\s*info/gi,
        '<button class="qv-care-link" type="button">⟡ STERLING SILVER CARE &amp; INFO</button>'
      );

      qvContent.innerHTML = `
        <div class="quick-view-inner">
          <div class="quick-view-img-wrap">
            ${imgSrc ? `<img src="${safeUrl(imgSrc)}" alt="${esc(product.title)}" class="quick-view-img" loading="lazy">` : ''}
          </div>
          <div class="qv-right">
            <h2 class="quick-view-title">${esc(product.title).toUpperCase()}</h2>
            <p class="quick-view-price" id="qv-price">${money(variant.price)}</p>
            ${rawDesc ? `<div class="quick-view-desc">${truncDesc}</div>` : ''}

            <div class="qv-care-panel" id="qv-care-panel" hidden>
              <button class="qv-care-panel-close" id="qv-care-close" type="button" aria-label="Close">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <p class="qv-care-title">STERLING SILVER CARE &amp; INFO</p>
              <ul class="qv-care-list">
                <li>Store in a cool, dry place away from direct sunlight</li>
                <li>Avoid contact with perfumes, lotions, and chemicals</li>
                <li>Remove before swimming, showering, or exercising</li>
                <li>Polish gently with a soft silver cloth to restore shine</li>
                <li>.925 Sterling Silver may naturally tarnish over time — this is normal and easily reversed</li>
                <li>Everyone's skin chemistry is different — people with higher skin acidity (lower pH) may notice their silver tarnishes faster than others. This is completely natural and not a defect in the piece</li>
                <li>If your skin tends to tarnish silver quickly, removing your jewelry before bed and wiping it down after wear will significantly slow the process</li>
              </ul>
            </div>

            ${hasVariants ? `
              <div class="qv-select-wrap">
                <label class="qv-select-label">${esc(firstOption).toUpperCase()}</label>
                <div class="qv-select-outer">
                  <select class="qv-select" id="qv-size-select">
                    ${product.variants.map(v => `
                      <option
                        value="${esc(v.id)}"
                        data-price="${esc(v.price)}"
                        ${v.id === variant.id ? 'selected' : ''}
                        ${!v.available ? 'disabled' : ''}
                      >${esc(v.title)}${!v.available ? ' — Sold Out' : ''}</option>
                    `).join('')}
                  </select>
                  <svg class="select-arrow" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke="#a89060" stroke-width="1.5"/></svg>
                </div>
              </div>
            ` : ''}

            <div style="margin-top:20px">
              <button class="btn-atc-v2 qv-atc-btn" data-variant-id="${variant.id}">
                ADD TO BAG
              </button>
            </div>
            <a href="${safeUrl(productUrl)}" class="quick-view-link">VIEW FULL PRODUCT →</a>
          </div>
        </div>
      `;

      /* Size dropdown — quick view */
      let selectedVariantId = variant.id;
      const qvSelect = qs('#qv-size-select', qvContent);
      if (qvSelect) {
        qvSelect.addEventListener('change', () => {
          const opt = qvSelect.options[qvSelect.selectedIndex];
          selectedVariantId = parseInt(opt.value, 10);
          const priceEl = qs('#qv-price', qvContent);
          if (priceEl) priceEl.textContent = money(parseInt(opt.dataset.price, 10));
          const atcBtn = qs('.qv-atc-btn', qvContent);
          if (atcBtn) atcBtn.dataset.variantId = selectedVariantId;
        });
      }

      /* Care info panel toggle */
      const careLink  = qs('.qv-care-link', qvContent);
      const carePanel = qs('#qv-care-panel', qvContent);
      const careClose = qs('#qv-care-close', qvContent);
      if (careLink && carePanel) {
        careLink.addEventListener('click', () => { carePanel.hidden = !carePanel.hidden; });
      }
      if (careClose && carePanel) {
        careClose.addEventListener('click', () => { carePanel.hidden = true; });
      }

      /* add to cart from quick view */
      qs('.qv-atc-btn', qvContent)?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        btn.textContent = '[ ADDING... ]';
        btn.classList.add('loading');
        try {
          await cartAdd(parseInt(btn.dataset.variantId, 10), 1);
          await fetchCart();
          btn.textContent = '[ ADDED ✓ ]';
          renderCartDrawer();
          updateCartCount();
          setTimeout(() => {
            closeModal(qvModal);
            openCartDrawer();
          }, 600);
        } catch (err) {
          btn.textContent = '[ ERROR ]';
          setTimeout(() => { btn.textContent = '[ ADD TO BAG ]'; btn.classList.remove('loading'); }, 2000);
        }
      });

    } catch (err) {
      qvContent.innerHTML = '<p style="padding:40px;text-align:center;font-family:var(--font-display);font-size:0.7rem;letter-spacing:0.2em;color:var(--text-muted)">COULD NOT LOAD PRODUCT</p>';
    }
  }

  /* delegate quick view clicks */
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-quick-view]');
    if (btn) {
      e.preventDefault();
      const handle = btn.dataset.quickView;
      const card = btn.closest('[data-product]');
      let embedded = null;
      if (card && card.dataset.product) {
        try { embedded = JSON.parse(card.dataset.product); } catch (_) {}
      }
      openQuickView(handle, embedded);
    }
  });

  /* ─── ADD TO CART — PRODUCT PAGE ─── */
  const productForm = qs('#product-form');
  const atcBtn = qs('#atc-btn');

  if (productForm) {
    productForm.addEventListener('submit', async e => {
      e.preventDefault();
      const variantId = parseInt(qs('#variant-id').value, 10);
      const qty = parseInt(qs('#form-quantity, #qty-input')?.value || '1', 10);
      if (!variantId) return;

      const btn = qs('#atc-btn');
      const origText = btn.textContent;
      btn.textContent = 'ADDING...';
      btn.classList.add('loading');
      btn.disabled = true;

      try {
        await cartAdd(variantId, qty);
        await fetchCart();
        renderCartDrawer();
        updateCartCount();
        btn.textContent = 'ADDED ✓';
        showNotification('Added to bag!');
        setTimeout(() => {
          openCartDrawer();
          btn.textContent = origText;
          btn.disabled = false;
          btn.classList.remove('loading');
        }, 800);
      } catch (err) {
        btn.textContent = 'UNAVAILABLE';
        setTimeout(() => {
          btn.textContent = origText;
          btn.disabled = false;
          btn.classList.remove('loading');
        }, 2000);
      }
    });
  }

  /* ─── VARIANT SELECTION — PRODUCT PAGE (dropdown style) ─── */
  const productData = window.__productData;
  if (productData) {
    let selectedOptions = productData.variants[0]?.options?.slice() || [];

    function syncVariant() {
      const matched = productData.variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      ) || productData.variants.find(v =>
        v.options.some((opt, i) => opt === selectedOptions[i])
      );
      if (!matched) return;

      qs('#variant-id').value = matched.id;

      const priceEl = qs('#product-price');
      if (priceEl) {
        priceEl.innerHTML = matched.compare_at_price > matched.price
          ? `<span class="price-sale">${money(matched.price)}</span><span class="price-original">${money(matched.compare_at_price)}</span>`
          : money(matched.price);
      }

      const btn = qs('#atc-btn');
      if (btn) {
        btn.disabled = !matched.available;
        btn.textContent = matched.available ? 'ADD TO BAG' : 'SOLD OUT';
      }

      const availEl = qs('#product-availability');
      if (availEl) {
        availEl.innerHTML = matched.available
          ? '<span class="avail-dot avail-dot--in"></span> In stock'
          : '<span class="avail-dot avail-dot--out"></span> Sold out';
      }

      if (matched.featured_image) {
        const mainImg = qs('#main-product-image');
        if (mainImg) mainImg.src = matched.featured_image.src;
      }
    }

    /* Dropdown selects */
    qsa('.variant-select').forEach(sel => {
      sel.addEventListener('change', () => {
        const pos = parseInt(sel.dataset.optionPosition, 10) - 1;
        selectedOptions[pos] = sel.value;
        syncVariant();
      });
    });

    /* Legacy pill buttons (quick view uses these) */
    qsa('.option-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
        selectedOptions[pos] = btn.dataset.value;
        qsa(`.option-btn[data-option-position="${btn.dataset.optionPosition}"]`).forEach(b => b.classList.remove('option-btn--active'));
        btn.classList.add('option-btn--active');
        syncVariant();
      });
    });
  }

  /* Product page image thumbnails (new sm style) */
  qsa('.product-thumb-sm').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const mainImg = qs('#main-product-image');
      if (mainImg) {
        mainImg.removeAttribute('srcset');
        mainImg.src = thumb.dataset.src;
      }
      qsa('.product-thumb-sm').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
    });
  });

  /* Legacy thumbnails */
  qsa('.product-thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      const mainImg = qs('#main-product-image');
      if (mainImg) mainImg.src = thumb.dataset.src;
      qsa('.product-thumb').forEach(t => t.classList.remove('product-thumb--active'));
      thumb.classList.add('product-thumb--active');
    });
  });

  /* Product page qty buttons */
  const qtyInput = qs('#qty-input');
  qs('#qty-up')?.addEventListener('click', () => { if (qtyInput) qtyInput.value = parseInt(qtyInput.value, 10) + 1; });
  qs('#qty-down')?.addEventListener('click', () => { if (qtyInput && parseInt(qtyInput.value, 10) > 1) qtyInput.value = parseInt(qtyInput.value, 10) - 1; });

  /* ─── MOBILE MENU ─── */
  const mobileMenuBtn = qs('#mobile-menu-btn');
  const mobileNav = qs('#mobile-nav');

  if (mobileMenuBtn && mobileNav) {
    mobileMenuBtn.addEventListener('click', () => {
      const isOpen = mobileNav.classList.toggle('open');
      mobileMenuBtn.classList.toggle('open', isOpen);
      mobileMenuBtn.setAttribute('aria-expanded', isOpen);
    });
  }

  /* ─── STICKY HEADER SHADOW ─── */
  const header = qs('#site-header');
  if (header) {
    const observer = new IntersectionObserver(
      ([entry]) => { header.classList.toggle('scrolled', !entry.isIntersecting); },
      { rootMargin: '-1px 0px 0px 0px', threshold: 0 }
    );
    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none';
    document.body.prepend(sentinel);
    observer.observe(sentinel);
  }

  /* ─── ATC NOTIFICATION ─── */
  function showNotification(msg) {
    let n = qs('.atc-notification');
    if (!n) {
      n = document.createElement('div');
      n.className = 'atc-notification';
      document.body.appendChild(n);
    }
    n.textContent = msg;
    n.classList.add('show');
    setTimeout(() => n.classList.remove('show'), 2500);
  }

  /* ─── UPDATE CART COUNT ─── */
  function updateCartCount() {
    qsa('#cart-count, #cart-drawer-count').forEach(el => {
      el.textContent = cart.item_count;
    });
  }

  /* ─── ESC KEY ─── */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    if (cartDrawer?.classList.contains('open'))    closeModal(cartDrawer);
    if (searchModal?.classList.contains('open'))   closeModal(searchModal);
    if (qvModal?.classList.contains('open'))       closeModal(qvModal);
    if (shippingModal?.classList.contains('open')) closeModal(shippingModal);
    if (careModal?.classList.contains('open'))     closeModal(careModal);
  });

  /* ─── PRODUCT CARE ACCORDIONS ─── */
  qsa('.js-care-toggle').forEach(toggle => {
    toggle.addEventListener('click', () => {
      const body   = toggle.nextElementSibling;
      const isOpen = !body.hidden;
      body.hidden  = isOpen;
      toggle.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });

  /* Inline description care link → open care modal */
  const careModal        = qs('#care-modal');
  const careModalClose   = qs('#care-modal-close');
  const careModalOverlay = qs('#care-modal-overlay');

  document.addEventListener('click', e => {
    if (e.target.closest('.js-care-inline-trigger') && careModal) {
      openModal(careModal);
    }
  });
  careModalClose?.addEventListener('click',   () => closeModal(careModal));
  careModalOverlay?.addEventListener('click', () => closeModal(careModal));

  /* legacy single-id toggle */
  const careToggle = qs('#product-care-toggle');
  const careBody   = qs('#product-care-body');
  if (careToggle && careBody) {
    careToggle.addEventListener('click', () => {
      const isOpen = !careBody.hidden;
      careBody.hidden = isOpen;
      careToggle.setAttribute('aria-expanded', (!isOpen).toString());
    });
  }

  /* ─── SHIPPING MODAL ─── */
  const shippingModal   = qs('#shipping-modal');
  const shippingTrigger = qs('#shipping-info-trigger');
  const shippingClose   = qs('#shipping-modal-close');
  const shippingOverlay = qs('#shipping-modal-overlay');

  if (shippingTrigger && shippingModal) {
    shippingTrigger.addEventListener('click', () => openModal(shippingModal));
    shippingClose?.addEventListener('click',  () => closeModal(shippingModal));
    shippingOverlay?.addEventListener('click', () => closeModal(shippingModal));
  }

  /* ─── COLLECTION SLIDESHOW ─── */
  (function () {
    const slideshow = document.getElementById('coll-slideshow');
    if (!slideshow) return;

    const slides = qsa('.coll-slide', slideshow);
    const dots   = qsa('.coll-dot', slideshow);
    if (slides.length < 2) return;

    let current = 0;
    let timer;

    function goTo(index) {
      slides[current].classList.remove('coll-slide--active');
      dots[current].classList.remove('coll-dot--active');
      current = (index + slides.length) % slides.length;
      slides[current].classList.add('coll-slide--active');
      dots[current].classList.add('coll-dot--active');
    }

    function start() { timer = setInterval(() => goTo(current + 1), 4800); }
    function stop()  { clearInterval(timer); }

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { stop(); goTo(i); start(); });
    });

    slideshow.addEventListener('mouseenter', stop);
    slideshow.addEventListener('mouseleave', start);

    start();
  })();

  /* ─── INIT ─── */
  fetchCart().then(() => {
    updateCartCount();
    renderCartDrawer();
  });

})();
